import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "statusBadge", "statusSection", "statusMessage", "verifySection",
    "step1", "step2", "requestBtn", "submitBtn", "codeInput",
    "loading", "loadingText", "error", "errorText"
  ]

  connect() {
    this.checkStatus()
  }

  async checkStatus() {
    try {
      const response = await fetch('/sp/submithub/status', {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()

      if (!data.configured) {
        this.statusBadgeTarget.textContent = 'Not Configured'
        this.statusBadgeTarget.className = 'sh-status-badge sh-status-unconfigured'
        this.statusMessageTarget.textContent = 'SubmitHub credentials are not configured. Contact your administrator.'
        this.verifySectionTarget.style.display = 'none'
      } else if (data.needs_verification) {
        this.statusBadgeTarget.textContent = 'Verification Needed'
        this.statusBadgeTarget.className = 'sh-status-badge sh-status-needs-verification'
        this.statusMessageTarget.textContent = data.pending_count > 0
          ? `${data.pending_count} playlists waiting for bot verification.`
          : 'Session expired. Please verify to enable bot checking.'
        this.verifySectionTarget.style.display = 'block'
      } else {
        this.statusBadgeTarget.textContent = 'Active'
        this.statusBadgeTarget.className = 'sh-status-badge sh-status-active'
        this.statusMessageTarget.textContent = 'Your SubmitHub session is active. Bot checking is enabled.'
        this.verifySectionTarget.style.display = 'none'
      }
    } catch (error) {
      this.statusBadgeTarget.textContent = 'Error'
      this.statusBadgeTarget.className = 'sh-status-badge sh-status-error'
      this.statusMessageTarget.textContent = 'Could not check status. Please refresh the page.'
    }
  }

  showStep(stepNum) {
    this.step1Target.style.display = stepNum === 1 ? 'block' : 'none'
    this.step2Target.style.display = stepNum === 2 ? 'block' : 'none'
    this.hideError()
  }

  backToStep1() {
    this.showStep(1)
  }

  showLoading(text = 'Processing...') {
    this.loadingTarget.style.display = 'flex'
    this.loadingTextTarget.textContent = text
  }

  hideLoading() {
    this.loadingTarget.style.display = 'none'
  }

  showError(message) {
    this.errorTarget.style.display = 'block'
    this.errorTextTarget.textContent = message
  }

  hideError() {
    this.errorTarget.style.display = 'none'
  }

  async requestCode() {
    this.hideError()
    this.requestBtnTarget.disabled = true
    this.showLoading('Sending verification code...')

    try {
      const response = await fetch('/sp/submithub/request_code', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const data = await response.json()

      if (data.success) {
        this.runId = data.run_id
        this.pollForCodeSent()
      } else {
        this.hideLoading()
        this.showError(data.error || 'Failed to request verification code')
        this.requestBtnTarget.disabled = false
      }
    } catch (error) {
      this.hideLoading()
      this.showError('Network error. Please try again.')
      this.requestBtnTarget.disabled = false
    }
  }

  async pollForCodeSent() {
    let attempts = 0
    const maxAttempts = 30

    const poll = async () => {
      attempts++

      try {
        const response = await fetch(`/sp/submithub/check_verification?run_id=${this.runId}`, {
          headers: { 'Accept': 'application/json' }
        })
        const data = await response.json()

        if (data.complete) {
          this.hideLoading()
          this.requestBtnTarget.disabled = false

          if (data.already_valid) {
            // Session already valid, refresh status to show active
            this.checkStatus()
          } else if (data.needs_code) {
            // Code was sent, show code input
            this.showStep(2)
            this.codeInputTarget.focus()
          } else if (data.success && !data.needs_code) {
            // Verification successful (shouldn't happen in this flow)
            this.checkStatus()
          } else {
            this.showError(data.message || 'Failed to send verification code')
          }
        } else if (attempts < maxAttempts) {
          setTimeout(poll, 2000)
        } else {
          this.hideLoading()
          this.requestBtnTarget.disabled = false
          this.showStep(2)
          this.codeInputTarget.focus()
        }
      } catch (error) {
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000)
        } else {
          this.hideLoading()
          this.requestBtnTarget.disabled = false
          this.showError('Timeout. Please try again.')
        }
      }
    }

    poll()
  }

  onCodeInput(event) {
    event.target.value = event.target.value.replace(/\D/g, '').slice(0, 6)
  }

  onCodeKeypress(event) {
    if (event.key === 'Enter' && this.codeInputTarget.value.length === 6) {
      this.submitCode()
    }
  }

  async submitCode() {
    const code = this.codeInputTarget.value.trim()

    if (code.length !== 6) {
      this.showError('Please enter a 6-digit code')
      return
    }

    this.hideError()
    this.submitBtnTarget.disabled = true
    this.showLoading('Verifying code...')

    try {
      const response = await fetch('/sp/submithub/verify_code', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ code: code })
      })

      const data = await response.json()

      if (data.success) {
        this.runId = data.run_id
        this.pollForVerificationResult()
      } else {
        this.hideLoading()
        this.showError(data.error || 'Failed to verify code')
        this.submitBtnTarget.disabled = false
      }
    } catch (error) {
      this.hideLoading()
      this.showError('Network error. Please try again.')
      this.submitBtnTarget.disabled = false
    }
  }

  async pollForVerificationResult() {
    let attempts = 0
    const maxAttempts = 30

    const poll = async () => {
      attempts++

      try {
        const response = await fetch(`/sp/submithub/check_verification?run_id=${this.runId}`, {
          headers: { 'Accept': 'application/json' }
        })
        const data = await response.json()

        if (data.complete) {
          this.hideLoading()
          this.submitBtnTarget.disabled = false

          if (data.success) {
            this.showStep(1)
            this.codeInputTarget.value = ''
            this.checkStatus() // Refresh status to show active
          } else {
            this.showError(data.message || 'Verification failed. Code may be invalid or expired.')
            this.codeInputTarget.value = ''
            this.codeInputTarget.focus()
          }
        } else if (attempts < maxAttempts) {
          setTimeout(poll, 2000)
        } else {
          this.hideLoading()
          this.submitBtnTarget.disabled = false
          this.showError('Timeout. Please try again.')
        }
      } catch (error) {
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000)
        } else {
          this.hideLoading()
          this.submitBtnTarget.disabled = false
          this.showError('Network error. Please try again.')
        }
      }
    }

    poll()
  }
}
